/* istanbul ignore file */
import { CreatePageLayout, FormSection, FormRow, BigFormRow } from 'framework/ui/layout';
import { CreateForm, AddressInput, BELARUS } from 'framework/form';
import { ContactInfoSection, HrInfoSection, MainInfoSection, RequisitesSection } from './sections';
import {
  useEmployeeService,
  DOCUMENTS_ALIAS,
  UPDATE_EMPLOYEE_PEOPLE_MANAGEMENT_RULES,
  useConversionRouteHelper,
  useEmployeeUrl,
  EMPLOYEE_STATE_KEYS,
} from 'domain/shared/employee';
import { useCandidateConversionService } from 'domain/shared/candidate';
import { useAuthorization } from 'framework/authorization';
import { Loading } from 'framework/ui/mui-builder';
import CreateEmployeeBreadcrumbs from 'domain/people/employee/createupdate/Create/Breadcrumbs';
import ConvertCandidateDropdown from 'domain/people/employee/shared/input/ConvertCandidateDropdown';
import PeopleManagementSpecifySection from 'domain/people/employee/shared/input/PeopleManagementSpecifySection';
import useConversionPositionSlot from './useConversionPositionSlot';
import { applyPositionSlotToInitialData } from './conversionPositionSlotFormData';
import IdentityDocumentListInput from '../../shared/input/IdentityDocumentInput/IdentityDocumentListInput';
import IdentityDocumentInput from '../../shared/input/IdentityDocumentInput/IdentityDocumentInput';

const CreateEmployeePage = () => {
  const { create, getValidationSchema } = useEmployeeService();
  const { getCandidate, mapCandidateToEmployee, convertCandidate } = useCandidateConversionService();
  const { candidateId } = useConversionRouteHelper();
  const { getListUrl } = useEmployeeUrl();
  const { isGranted } = useAuthorization();

  const submitMethod = candidateId ? convertCandidate : create;

  const { loading } = getCandidate();
  const { positionSlot, isLoading: isPositionSlotLoading } = useConversionPositionSlot(candidateId);

  if (loading || isPositionSlotLoading) {
    return <Loading />;
  }

  const initialData = applyPositionSlotToInitialData(
    mapCandidateToEmployee({
      initializationState: EMPLOYEE_STATE_KEYS.NEW,
      extendedInformation: {
        documentType: DOCUMENTS_ALIAS.PASSPORT,
        passportData: {
          citizenship: BELARUS.name,
        },
        registrationAddress: {
          country: BELARUS.name,
        },
        additionalContactInfo: [],
      },
      assignedPeopleManagerAliases: [],
      isAccessForAllPeopleManagers: false,
    }),
    positionSlot,
  );

  return (
    <CreateForm
      submitFormFunc={submitMethod}
      initialData={initialData}
      afterSubmit={{
        redirect: getListUrl(),
        message: 'You are awesome! The Worker has been successfully created.',
      }}
      getValidationSchemaFunc={getValidationSchema}
    >
      <CreatePageLayout breadcrumbs={<CreateEmployeeBreadcrumbs />} title="Create: Worker">
        <FormSection title="Convert from candidate">
          <FormRow>
            <ConvertCandidateDropdown source="candidateId" label="Candidate" />
          </FormRow>
        </FormSection>
        <MainInfoSection />
        <HrInfoSection positionSlot={positionSlot} />
        <FormSection title="People Management Rules" isVisible={isGranted(UPDATE_EMPLOYEE_PEOPLE_MANAGEMENT_RULES)}>
          <PeopleManagementSpecifySection />
        </FormSection>

        <FormSection title="Identifiers" hasAnchorLink>
          <BigFormRow>
            <IdentityDocumentListInput
              source="extendedInformation.identityDocuments"
              inputTemplate={IdentityDocumentInput}
              addText="Add document"
            />
          </BigFormRow>
        </FormSection>
        <ContactInfoSection />
        <FormSection title="Registration address">
          <AddressInput source="extendedInformation.registrationAddress" />
        </FormSection>
        <RequisitesSection />
      </CreatePageLayout>
    </CreateForm>
  );
};

export default CreateEmployeePage;
